import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { dataService } from "../../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import Button from "../../../../Shared/Buttons/Button";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../../Shared/Select";
import { Plus, Search } from "lucide-react";
import ResetFiltersButton from "../../../../Filter/ResetFiltersButton";
import { crudTypes, AccountFilterTypes } from "../../../../../Config/constants";
import TablePagination from "@mui/material/TablePagination";
import useFilterPersistence from "../../../../../hooks/useFilterPersistence";
// Table
import AccountTable from "../Account/AccountTable";
// Dialogs (shared with the legacy Account Overview page)
import CreateAccountDialog from "../../../AccountOverview/Components/CreateAccountDialog";
import CreateAccountCategoryDialog from "../../../AccountOverview/Components/CreateAccountCategoryDialog";
import DeleteConfirmDialog from "../../../AccountOverview/Components/DeleteConfirmDialog";

function AccountTab(props) {
  const { t } = useTranslation();
  const classes = useStyles();

  // General
  const [originalAccounts, setOriginalAccounts] = useState([]); // Flattened, unfiltered list of accounts
  const [accountList, setAccountList] = useState([]); // Filtered and sorted
  const [companiesWithCategories, setCompaniesWithCategories] = useState([]); // Used by the create/edit dialogs and the company filter
  const { filters, setFilter, setFilterText, resetFilters, getFilterValue, hasActiveFilters, page, setPage, pageSize, setPageSize } = useFilterPersistence({
    pageKey: "financial-accounts",
    filterSchema: { acc_search: AccountFilterTypes.SEARCH, acc_company: AccountFilterTypes.COMPANY },
    defaultPageSize: 25,
    persistPageSize: true,
  });

  // Dialogs
  const [createAccountCategoryDialogOpen, setCreateAccountCategoryDialogOpen] = useState(false);
  const [createAccountDialogOpen, setCreateAccountDialogOpen] = useState(false);
  const [createAccountCompanyId, setCreateAccountCompanyId] = useState(null);
  const [accountToEdit, setAccountToEdit] = useState(null);

  // Delete
  const [deleteDialog, setDeleteDialog] = useState({ open: false, target: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Flatten the company -> category -> account hierarchy into a single list of account rows
  const flattenAccounts = (companies) => {
    const rows = [];
    (companies || []).forEach((company) => {
      (company.accountCategories || []).forEach((category) => {
        (category.accounts || []).forEach((account) => {
          rows.push({
            id: account.id,
            name: account.name,
            isActive: account.isActive,
            accountCategoryId: category.id,
            categoryName: category.name,
            isRevenue: category.isRevenue,
            companyId: company.id,
            companyName: company.name,
          });
        });
      });
    });
    return rows;
  };

  // Fetch accounts
  useEffect(() => {
    dataService
      .getAllUserCompanyAccounts(props.Login.userid, 0, 0)
      .then((response) => {
        setOriginalAccounts(flattenAccounts(response));
      })
      .catch((error) => {
        console.error("Error fetching accounts:", error);
      });
  }, [props.Login.userid]);

  // Fetch companies + categories (drives the company filter and the create/edit dialogs)
  useEffect(() => {
    dataService
      .getAllUserCompanyAccountCategories(props.Login.userid, 0, 0)
      .then((response) => {
        setCompaniesWithCategories(response || []);
      })
      .catch((error) => {
        console.error("Error fetching account categories:", error);
      });
  }, [props.Login.userid]);

  const filterList = (thisFilters) => {
    let varFilteredlist = [...originalAccounts];

    for (let i = 0; i < thisFilters.length; i++) {
      let filter = thisFilters[i];
      switch (filter.type) {
        case AccountFilterTypes.SEARCH:
          varFilteredlist = varFilteredlist.filter(
            (x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()) || (x.categoryName || "").toLowerCase().includes(filter.filterValue.toLowerCase())
          );
          break;
        case AccountFilterTypes.NAME:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case AccountFilterTypes.COMPANY:
          varFilteredlist = varFilteredlist.filter((x) => x.companyId === parseInt(filter.filterValue) || filter.filterValue === "");
          break;
        default:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
      }
    }

    // Sort alphabetically by name
    varFilteredlist = varFilteredlist.sort((a, b) => a.name.localeCompare(b.name));

    setAccountList(varFilteredlist);
  };

  // Re-apply filters whenever source data or active filters change (covers reset + URL restore)
  useEffect(() => {
    filterList(filters);
  }, [originalAccounts, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Dialog open/close ----
  const openCreateAccountCategoryDialog = () => {
    setCreateAccountCategoryDialogOpen(true);
  };

  const closeCreateAccountCategoryDialog = () => {
    setCreateAccountCategoryDialogOpen(false);
  };

  const openCreateAccountDialog = (companyId) => {
    setCreateAccountCompanyId(typeof companyId === "number" ? companyId : null);
    setCreateAccountDialogOpen(true);
  };

  const closeCreateAccountDialog = () => {
    setCreateAccountDialogOpen(false);
    setCreateAccountCompanyId(null);
    setAccountToEdit(null);
  };

  const handleEditAccount = (accountId) => {
    const account = originalAccounts.find((x) => x.id === accountId);
    if (!account) {
      console.log("Account not found.");
      return;
    }
    setAccountToEdit({
      id: account.id,
      name: account.name,
      companyId: account.companyId,
      accountCategoryId: account.accountCategoryId,
    });
  };

  // ---- Delete ----
  const handleDeleteAccount = (accountId) => {
    const account = originalAccounts.find((x) => x.id === accountId);
    if (!account) {
      console.log("Account not found.");
      return;
    }
    setDeleteDialog({ open: true, target: account });
    setDeleteError(null);
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, target: null });
    setDeleteError(null);
  };

  const handleConfirmDelete = () => {
    const account = deleteDialog.target;
    if (!account) return;
    setDeleteLoading(true);
    dataService.deleteAccount(account.id, props.Login.userid).then((response) => {
      setDeleteLoading(false);
      if (response && response.success) {
        toast.success(response.message);
        setOriginalAccounts((prev) => prev.filter((x) => x.id !== account.id));
        closeDeleteDialog();
      } else {
        setDeleteError(response ? response.message : t('accountTab.deleteError'));
      }
    });
  };

  // ---- Helpers ----
  const getCompanyName = (companyId) => {
    return companiesWithCategories.find((c) => c.id === companyId)?.name || "";
  };

  // Add a newly created category to companiesWithCategories so the dialogs immediately reflect it
  const addCategoryToCompanies = (category) => {
    setCompaniesWithCategories((prev) =>
      prev.map((company) => {
        if (company.id !== category.companyId) return company;
        const accountCategories = company.accountCategories || [];
        if (accountCategories.some((c) => c.id === category.id)) return company;
        return {
          ...company,
          accountCategories: [...accountCategories, { ...category, accounts: category.accounts || [] }].sort((a, b) => a.name.localeCompare(b.name)),
        };
      })
    );
  };

  // ---- CRUD success handlers ----
  const buildRow = (accountData) => {
    const account = accountData.accounts[0];
    return {
      id: account.id,
      name: account.name,
      isActive: account.isActive !== undefined ? account.isActive : true,
      accountCategoryId: accountData.accountCategoryId,
      categoryName: accountData.accountCategoryName,
      isRevenue: accountData.accountCategoryIsRevenue,
      companyId: accountData.companyId,
      companyName: getCompanyName(accountData.companyId),
    };
  };

  const handleAccountCrudSuccess = (accountData, crudType) => {
    // A brand-new category may have been created from within the account dialog
    if (accountData.isNewAccountCategory) {
      addCategoryToCompanies({
        id: accountData.accountCategoryId,
        name: accountData.accountCategoryName,
        isRevenue: accountData.accountCategoryIsRevenue,
        isActive: true,
        companyId: accountData.companyId,
        accounts: [],
      });
    }

    const row = buildRow(accountData);

    switch (crudType) {
      case crudTypes.CREATE:
        setOriginalAccounts((prev) => [...prev, row]);
        break;
      case crudTypes.UPDATE:
        setOriginalAccounts((prev) => prev.map((x) => (x.id === row.id ? row : x)));
        break;
      default:
        console.error(`Unsupported CRUD operation: ${crudType}`);
        break;
    }
  };

  const handleAccountCategoryCrudSuccess = (category, crudType) => {
    switch (crudType) {
      case crudTypes.CREATE:
        addCategoryToCompanies(category);
        break;
      case crudTypes.UPDATE:
        // Reflect a renamed / re-typed category on its accounts and in the dialog data
        setCompaniesWithCategories((prev) =>
          prev.map((company) => {
            if (company.id !== category.companyId) return company;
            return {
              ...company,
              accountCategories: (company.accountCategories || []).map((c) => (c.id === category.id ? { ...c, name: category.name, isRevenue: category.isRevenue } : c)),
            };
          })
        );
        setOriginalAccounts((prev) => prev.map((x) => (x.accountCategoryId === category.id ? { ...x, categoryName: category.name, isRevenue: category.isRevenue } : x)));
        break;
      default:
        break;
    }
  };

  // ---- Filter helpers ----
  const getSearchValue = () => {
    return getFilterValue(AccountFilterTypes.SEARCH);
  };

  const getSelectedCompany = () => {
    const companyId = getFilterValue(AccountFilterTypes.COMPANY);
    return companiesWithCategories.find((company) => company.id === parseInt(companyId)) || null;
  };

  // Calculate paginated rows
  const paginatedAccounts = accountList.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <React.Fragment>
      <div className={classes.tableManagementContainer}>
        <div className={classes.tableManagementContainer__start}>
          <div className={classes.searchContainer}>
            <input
              placeholder={t('accountTab.searchPlaceholder')}
              className={classes.search__input}
              type="text"
              value={getFilterValue(AccountFilterTypes.SEARCH)}
              onChange={(e) => setFilterText(AccountFilterTypes.SEARCH, e.target.value)}
            />
            <Search size={16} className={classes.inputIcon} />
          </div>
          <div className={classes.filterContainer}>
            <Select value={getSelectedCompany()?.id} onChange={(company) => setFilter(AccountFilterTypes.COMPANY, company?.id ?? "")} size="small">
              <SelectTrigger>{getSelectedCompany()?.name || t('accountTab.allCompanies')}</SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>{t('accountTab.allCompanies')}</SelectItem>
                {companiesWithCategories.map((company) => (
                  <SelectItem key={company.id} value={company}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
        </div>
        <div className={classes.tableManagementContainer__end}>
          <Button size="small" variant="secondary" startIcon={<Plus />} onClick={() => openCreateAccountCategoryDialog()}>
            {t('accountTab.createAccountCategory')}
          </Button>
          <Button size="small" startIcon={<Plus />} onClick={() => openCreateAccountDialog()}>
            {t('accountTab.createAccount')}
          </Button>
        </div>
      </div>

      {/* Table */}
      <AccountTable accounts={paginatedAccounts} handleEditAccount={handleEditAccount} handleDeleteAccount={handleDeleteAccount} searchValue={getSearchValue()} />

      {/* Pagination */}
      <TablePagination
        component="div"
        count={accountList.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Dialogs */}
      <CreateAccountCategoryDialog
        dialogOpen={createAccountCategoryDialogOpen}
        closeDialog={closeCreateAccountCategoryDialog}
        accountCategoryToEdit={null}
        companies={companiesWithCategories}
        userId={props.Login.userid}
        onSuccess={handleAccountCategoryCrudSuccess}
      />
      <CreateAccountDialog
        dialogOpen={createAccountDialogOpen || accountToEdit}
        closeDialog={closeCreateAccountDialog}
        accountToEdit={accountToEdit}
        companies={companiesWithCategories}
        createAccountCompanyId={createAccountCompanyId}
        accountCategories={companiesWithCategories}
        userId={props.Login.userid}
        onSuccess={handleAccountCrudSuccess}
      />
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        error={deleteError}
        title={t('accountTab.deleteTitle')}
        item={deleteDialog.target?.name}
        consequences={deleteDialog.target ? [t('accountTab.deleteConsequence', { name: deleteDialog.target.name, category: deleteDialog.target.categoryName })] : []}
      />
    </React.Fragment>
  );
}

export default AccountTab;

const useStyles = makeStyles({
  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  filterContainer: {
    width: "auto",
    minWidth: "fit-content",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid var(--color-primary)",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },
});
